import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

export type FinderViewMode = "detailed" | "simple";
export type PlannerMode = "canvas" | "table-grid";
export type SeatingLayoutPieceType = "wall" | "divider" | "entrance" | "pillar" | "dance-floor" | "dj-booth" | "bar";
export type SeatingTableShape = "round" | "block";

export interface EventTableRow {
  id: string;
  table_label: string;
}

export interface EventSeatRow {
  id: string;
  event_table_id: string;
  seat_label: string;
}

export interface SeatFinderLookupResultRow {
  table_label: string;
  seat_label: string;
  guest_name?: string;
  table_guests?: string[];
}

export interface SeatFinderPlanConfig {
  finder_view_mode: FinderViewMode;
  finder_show_table_guests: boolean;
  finder_highlight_guest: boolean;
  planner_mode: PlannerMode;
}

export interface SeatingLayoutPiece {
  id: string;
  label: string;
  type: SeatingLayoutPieceType;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
}

export interface SeatingLayoutTable {
  id: string;
  name: string;
  capacity: number;
  x: number;
  y: number;
  width: number;
  height: number;
  shape: SeatingTableShape;
}

export interface SeatingLayoutAssignment {
  id: string;
  tableId: string;
  tableName: string;
  guestName: string;
}

export interface EventSeatingLayout extends SeatFinderPlanConfig {
  room_layout: SeatingLayoutPiece[];
  tables: SeatingLayoutTable[];
  assignments: SeatingLayoutAssignment[];
}

export interface EventSeatingLayoutV2 extends EventSeatingLayout {
  planner_version: number;
  planner_document_v2: Json | null;
}

export interface AttendingGuestRow {
  id: string;
  name: string;
}

export interface SeatFinderService {
  getActiveEventBySlug(slug: string): Promise<{ id: string; title: string | null } | null>;
  getEventTables(eventId: string): Promise<EventTableRow[]>;
  getEventSeats(eventId: string): Promise<EventSeatRow[]>;
  getSeatFinderPlan(eventId: string): Promise<Partial<SeatFinderPlanConfig> | null>;
  getEventSeatingLayout(eventId: string): Promise<EventSeatingLayout | null>;
  getEventSeatingLayoutV2(eventId: string): Promise<EventSeatingLayoutV2 | null>;
  getAttendingGuestsForSeating(eventId: string): Promise<AttendingGuestRow[]>;
  lookupGuestSeat(slug: string, guestName: string, viewMode: FinderViewMode): Promise<SeatFinderLookupResultRow[]>;
  saveSeatFinderPlanConfig(eventId: string, payload: Omit<SeatFinderPlanConfig, "planner_mode"> & { planner_mode?: PlannerMode }): Promise<void>;
  savePlannerMode(eventId: string, mode: PlannerMode): Promise<void>;
  saveEventSeatingLayout(eventId: string, payload: EventSeatingLayout): Promise<void>;
  saveEventSeatingLayoutV2(eventId: string, payload: Omit<EventSeatingLayoutV2, "planner_version">): Promise<void>;
}

const db = supabase as any;

const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value && typeof value === "object" && !Array.isArray(value));
const asNumber = (value: unknown, fallback: number) => {
  const parsed = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  return Number.isFinite(parsed) ? parsed : fallback;
};
const asString = (value: unknown, fallback: string) => (typeof value === "string" && value.trim() ? value : fallback);

const parseRoomLayout = (value: unknown): SeatingLayoutPiece[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry): SeatingLayoutPiece[] => {
    if (!isRecord(entry)) return [];
    const type = entry.type === "wall"
      || entry.type === "divider"
      || entry.type === "entrance"
      || entry.type === "pillar"
      || entry.type === "dance-floor"
      || entry.type === "dj-booth"
      || entry.type === "bar"
      ? entry.type
      : "wall";
    return [{
      id: asString(entry.id, crypto.randomUUID()),
      label: asString(entry.label, "Wall"),
      type,
      x: asNumber(entry.x, 0),
      y: asNumber(entry.y, 0),
      width: asNumber(entry.width, 264),
      height: asNumber(entry.height, 24),
      rotation: asNumber(entry.rotation, 0),
    }];
  });
};

const parseTableStyle = (value: unknown) => (isRecord(value) ? value : {});

export const supabaseSeatFinderService: SeatFinderService = {
  async getActiveEventBySlug(slug) {
    const { data, error } = await db
      .from("events")
      .select("id, title")
      .eq("slug", slug)
      .eq("is_active", true)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;
    return { id: data.id, title: data.title ?? null };
  },

  async getEventTables(eventId) {
    const { data, error } = await db
      .from("event_tables")
      .select("id, table_label")
      .eq("event_id", eventId)
      .order("table_label", { ascending: true });

    if (error) throw error;
    return (data ?? []) as EventTableRow[];
  },

  async getEventSeats(eventId) {
    const { data, error } = await db
      .from("event_seats")
      .select("id, event_table_id, seat_label")
      .eq("event_id", eventId)
      .order("seat_label", { ascending: true });

    if (error) throw error;
    return (data ?? []) as EventSeatRow[];
  },

  async getSeatFinderPlan(eventId) {
    const { data, error } = await db
      .from("event_seating_plans")
      .select("finder_view_mode, finder_show_table_guests, finder_highlight_guest, planner_mode")
      .eq("event_id", eventId)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async getEventSeatingLayout(eventId) {
    const { data: plan, error: planError } = await db
      .from("event_seating_plans")
      .select("id, finder_view_mode, finder_show_table_guests, finder_highlight_guest, planner_mode, room_layout")
      .eq("event_id", eventId)
      .maybeSingle();

    if (planError) throw planError;
    if (!plan) return null;

    const [{ data: tableRows, error: tableError }, { data: seatRows, error: seatError }, { data: assignmentRows, error: assignmentError }] = await Promise.all([
      db
        .from("event_tables")
        .select("id, table_code, table_label, table_style, position_x, position_y")
        .eq("seating_plan_id", plan.id)
        .order("created_at", { ascending: true }),
      db
        .from("event_seats")
        .select("id, event_table_id, seat_index, seat_label")
        .eq("event_id", eventId)
        .order("seat_index", { ascending: true }),
      db
        .from("event_seat_assignments")
        .select("id, seat_id, guest_name")
        .eq("event_id", eventId)
        .eq("is_active", true)
        .order("created_at", { ascending: true }),
    ]);

    if (tableError) throw tableError;
    if (seatError) throw seatError;
    if (assignmentError) throw assignmentError;

    const tableNameById = new Map<string, string>();
    const tableCapacityById = new Map<string, number>();
    const seatTableById = new Map<string, string>();

    for (const seat of seatRows ?? []) {
      seatTableById.set(seat.id, seat.event_table_id);
      tableCapacityById.set(seat.event_table_id, Math.max(tableCapacityById.get(seat.event_table_id) ?? 0, Number(seat.seat_index) || 0));
    }

    const tables = (tableRows ?? []).map((table): SeatingLayoutTable => {
      const style = parseTableStyle(table.table_style);
      tableNameById.set(table.id, table.table_label);
      return {
        id: table.id,
        name: table.table_label,
        capacity: asNumber(style.capacity, tableCapacityById.get(table.id) ?? 8),
        x: asNumber(table.position_x, 0),
        y: asNumber(table.position_y, 0),
        width: asNumber(style.width, 120),
        height: asNumber(style.height, 120),
        shape: style.shape === "round" ? "round" : "block",
      };
    });

    const assignments = (assignmentRows ?? []).flatMap((assignment): SeatingLayoutAssignment[] => {
      const tableId = seatTableById.get(assignment.seat_id);
      if (!tableId) return [];
      return [{
        id: assignment.id,
        tableId,
        tableName: tableNameById.get(tableId) ?? "Table",
        guestName: assignment.guest_name,
      }];
    });

    return {
      planner_mode: plan.planner_mode === "table-grid" ? "table-grid" : "canvas",
      finder_view_mode: plan.finder_view_mode === "simple" ? "simple" : "detailed",
      finder_show_table_guests: Boolean(plan.finder_show_table_guests),
      finder_highlight_guest: Boolean(plan.finder_highlight_guest),
      room_layout: parseRoomLayout(plan.room_layout),
      tables,
      assignments,
    };
  },

  async getEventSeatingLayoutV2(eventId) {
    const { data: plan, error: planError } = await db
      .from("event_seating_plans")
      .select("id, finder_view_mode, finder_show_table_guests, finder_highlight_guest, planner_mode, room_layout, planner_version, planner_document_v2")
      .eq("event_id", eventId)
      .maybeSingle();

    if (planError) throw planError;
    if (!plan) return null;

    const [{ data: tableRows, error: tableError }, { data: seatRows, error: seatError }, { data: assignmentRows, error: assignmentError }] = await Promise.all([
      db
        .from("event_tables")
        .select("id, table_code, table_label, table_style, position_x, position_y")
        .eq("seating_plan_id", plan.id)
        .order("created_at", { ascending: true }),
      db
        .from("event_seats")
        .select("id, event_table_id, seat_index, seat_label")
        .eq("event_id", eventId)
        .order("seat_index", { ascending: true }),
      db
        .from("event_seat_assignments")
        .select("id, seat_id, guest_name")
        .eq("event_id", eventId)
        .eq("is_active", true)
        .order("created_at", { ascending: true }),
    ]);

    if (tableError) throw tableError;
    if (seatError) throw seatError;
    if (assignmentError) throw assignmentError;

    // The v2 RPC stores the planner's client-side table id in `table_code`, so prefer
    // that as the canonical id — it matches `metadata.tableId` in the DGM document and
    // keeps assignments addressable across save/reload cycles.
    const clientTableIdByServerId = new Map<string, string>();
    const tableNameById = new Map<string, string>();
    const tableCapacityByServerId = new Map<string, number>();
    const seatTableById = new Map<string, string>();

    for (const table of tableRows ?? []) {
      const clientTableId = (typeof table.table_code === "string" && table.table_code) ? table.table_code : table.id;
      clientTableIdByServerId.set(table.id, clientTableId);
    }

    for (const seat of seatRows ?? []) {
      seatTableById.set(seat.id, seat.event_table_id);
      tableCapacityByServerId.set(seat.event_table_id, Math.max(tableCapacityByServerId.get(seat.event_table_id) ?? 0, Number(seat.seat_index) || 0));
    }

    const tables = (tableRows ?? []).map((table): SeatingLayoutTable => {
      const style = parseTableStyle(table.table_style);
      const clientTableId = clientTableIdByServerId.get(table.id) ?? table.id;
      tableNameById.set(clientTableId, table.table_label);
      return {
        id: clientTableId,
        name: table.table_label,
        capacity: asNumber(style.capacity, tableCapacityByServerId.get(table.id) ?? 8),
        x: asNumber(table.position_x, 0),
        y: asNumber(table.position_y, 0),
        width: asNumber(style.width, 120),
        height: asNumber(style.height, 120),
        shape: style.shape === "round" ? "round" : "block",
      };
    });

    const assignments = (assignmentRows ?? []).flatMap((assignment): SeatingLayoutAssignment[] => {
      const serverTableId = seatTableById.get(assignment.seat_id);
      if (!serverTableId) return [];
      const clientTableId = clientTableIdByServerId.get(serverTableId) ?? serverTableId;
      return [{
        id: assignment.id,
        tableId: clientTableId,
        tableName: tableNameById.get(clientTableId) ?? "Table",
        guestName: assignment.guest_name,
      }];
    });

    return {
      planner_mode: plan.planner_mode === "table-grid" ? "table-grid" : "canvas",
      finder_view_mode: plan.finder_view_mode === "simple" ? "simple" : "detailed",
      finder_show_table_guests: Boolean(plan.finder_show_table_guests),
      finder_highlight_guest: Boolean(plan.finder_highlight_guest),
      planner_version: Number(plan.planner_version) || 1,
      planner_document_v2: (plan.planner_document_v2 ?? null) as Json | null,
      room_layout: parseRoomLayout(plan.room_layout),
      tables,
      assignments,
    };
  },

  async getAttendingGuestsForSeating(eventId) {
    const { data, error } = await db
      .from("event_guests")
      .select("id, name")
      .eq("event_id", eventId)
      .eq("attendance_status", "attending")
      .order("name", { ascending: true });

    if (error) throw error;
    return (data ?? []) as AttendingGuestRow[];
  },

  async lookupGuestSeat(slug, guestName, viewMode) {
    const rpcName = viewMode === "simple" ? "find_event_seat_with_table_guests" : "find_event_seat";
    const { data, error } = await db.rpc(rpcName, {
      p_event_slug: slug,
      p_guest_name: guestName,
    });

    if (error) throw error;
    return (data ?? []) as SeatFinderLookupResultRow[];
  },

  async saveSeatFinderPlanConfig(eventId, payload) {
    const { error } = await db
      .from("event_seating_plans")
      .upsert({
        event_id: eventId,
        plan_name: "default",
        ...payload,
      }, { onConflict: "event_id" });

    if (error) throw error;
  },

  async savePlannerMode(eventId, mode) {
    const { error } = await db
      .from("event_seating_plans")
      .upsert({
        event_id: eventId,
        plan_name: "default",
        planner_mode: mode,
      }, { onConflict: "event_id" });

    if (error) throw error;
  },

  async saveEventSeatingLayout(eventId, payload) {
    const tables = payload.tables.map((table) => ({
      client_id: table.id,
      table_code: table.id,
      table_label: table.name,
      position_x: table.x,
      position_y: table.y,
      table_style: {
        capacity: table.capacity,
        width: table.width,
        height: table.height,
        shape: table.shape,
      },
    }));

    const assignments = payload.assignments.map((assignment) => ({
      table_id: assignment.tableId,
      guest_name: assignment.guestName,
    }));

    const { error } = await db.rpc("save_event_seating_layout", {
      p_event_id: eventId,
      p_planner_mode: payload.planner_mode,
      p_finder_view_mode: payload.finder_view_mode,
      p_finder_show_table_guests: payload.finder_show_table_guests,
      p_finder_highlight_guest: payload.finder_highlight_guest,
      p_room_layout: payload.room_layout as unknown as Json,
      p_tables: tables as unknown as Json,
      p_assignments: assignments as unknown as Json,
    });

    if (error) throw error;
  },

  async saveEventSeatingLayoutV2(eventId, payload) {
    const tables = payload.tables.map((table) => ({
      client_id: table.id,
      table_code: table.id,
      table_label: table.name,
      position_x: table.x,
      position_y: table.y,
      table_style: {
        capacity: table.capacity,
        width: table.width,
        height: table.height,
        shape: table.shape,
      },
    }));

    const assignments = payload.assignments.map((assignment) => ({
      table_id: assignment.tableId,
      guest_name: assignment.guestName,
    }));

    const { error } = await db.rpc("save_event_seating_layout_v2", {
      p_event_id: eventId,
      p_planner_mode: payload.planner_mode,
      p_finder_view_mode: payload.finder_view_mode,
      p_finder_show_table_guests: payload.finder_show_table_guests,
      p_finder_highlight_guest: payload.finder_highlight_guest,
      p_planner_document_v2: payload.planner_document_v2,
      p_room_layout: payload.room_layout as unknown as Json,
      p_tables: tables as unknown as Json,
      p_assignments: assignments as unknown as Json,
    });

    if (error) throw error;
  },
};
